import { UseMutateFunction } from '@tanstack/react-query';
import { FC, ReactNode, createContext, useCallback, useContext } from 'react';

import { CHIRP_APP_URL, COGNITO_CLIENT_ID, COGNITO_URL } from 'config';
import { GetUserAuthTokensResponseDTO } from 'declarations/dto/auth';
import { StorageE } from 'declarations/enums/storage';
import { ApiErrorI } from 'declarations/interfaces/errors';
import { NoopT } from 'declarations/types/common';
import { useFetchUserAuthTokens } from 'hooks/api/useAuth';
import useAuthStore from 'hooks/store/useAuthStore';

export type AuthContextT = {
  isUserLoggedIn: boolean;
  login: UseMutateFunction<GetUserAuthTokensResponseDTO, ApiErrorI, string>;
  isLoginLoading: boolean;
  signOut: NoopT;
};

type AuthProviderPropsT = { children: ReactNode };

const contextDefaultValue = {
  isUserLoggedIn: false,
  isLoginLoading: false,
  login: () => Promise.resolve(null),
  signOut: () => Promise.resolve(false),
};

export const AuthContext = createContext<AuthContextT>(contextDefaultValue);

const AuthProvider: FC<AuthProviderPropsT> = ({ children }) => {
  /**
   * Hooks
   */
  const { isUserLoggedIn, setUserLoginStatus } = useAuthStore(state => state);

  /**
   * Api hooks
   */
  const createTokensMutation = useFetchUserAuthTokens({
    onSuccess: data => {
      localStorage.setItem(StorageE.IdToken, data.id_token);
      localStorage.setItem(StorageE.RefreshToken, data.refresh_token);
      setUserLoginStatus(true);
    },
  });

  /**
   * Handlers
   */
  const signOut = useCallback(() => {
    window.location.href = `${COGNITO_URL}/logout?client_id=${COGNITO_CLIENT_ID}&logout_uri=${CHIRP_APP_URL}/logout`;
    localStorage.removeItem(StorageE.IdToken);
    localStorage.removeItem(StorageE.RefreshToken);
    setUserLoginStatus(false);
  }, [setUserLoginStatus]);

  return (
    <AuthContext.Provider
      value={{
        isUserLoggedIn,
        login: createTokensMutation.mutate,
        isLoginLoading: createTokensMutation.isLoading,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
